// 章节提示词面板：所有登录用户可读、可划词批注；提示词正文只有管理员能改。
// 批注是全局共享的一份（挂在章节号上），所以这里不需要 conversationId。

import { useEffect, useState } from "react";
import { Download, Pencil, Save } from "lucide-react";
import { AnnotatedMarkdown, AnnotationSidebar } from "./AnnotatedMarkdown";
import {
  type PromptAnnotation,
  addPromptAnnotation,
  deletePromptAnnotation,
  downloadPromptsDocx,
  getPromptAnnotations,
  savePromptContent,
  setPromptAnnotationResolved,
} from "../promptClient";
import type { AuthUser } from "../authClient";
import type { Chapter } from "../data";

function formatTime(ts: number): string {
  return new Date(ts).toLocaleString("zh-CN", {
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export default function PromptPane({
  chapter,
  user,
  onPromptSaved,
}: {
  chapter: Chapter;
  user: AuthUser;
  /** 保存成功后把新提示词回传给上层，避免关掉弹窗再打开还是旧的。 */
  onPromptSaved: (chapterNum: string, prompt: string) => void;
}) {
  const isAdmin = user.role === "admin";
  const [content, setContent] = useState(chapter.prompt ?? "");
  const [savedContent, setSavedContent] = useState(chapter.prompt ?? "");
  const [annotations, setAnnotations] = useState<PromptAnnotation[]>([]);
  const [mode, setMode] = useState<"preview" | "edit">("preview");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [activeAnnoId, setActiveAnnoId] = useState<number | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    setMode("preview");
    setContent(chapter.prompt ?? "");
    setSavedContent(chapter.prompt ?? "");
    getPromptAnnotations(chapter.num)
      .then((list) => !cancelled && setAnnotations(list))
      .catch((err) => !cancelled && setError(err instanceof Error ? err.message : "加载批注失败"))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // 只跟章节号联动：chapter.prompt 仅作初值。保存后正文以接口返回为准，
    // 若把它也放进依赖，回传给上层刷新 chapters 会反过来把这里重置回预览模式。
  }, [chapter.num]);

  const dirty = content !== savedContent;

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      const saved = await savePromptContent(chapter.num, content);
      setContent(saved);
      setSavedContent(saved);
      onPromptSaved(chapter.num, saved);
    } catch (err) {
      setError(err instanceof Error ? err.message : "保存失败");
    } finally {
      setSaving(false);
    }
  }

  async function handleAdd(quote: string, comment: string) {
    try {
      const annotation = await addPromptAnnotation(chapter.num, quote, comment);
      setAnnotations((prev) => [...prev, annotation]);
    } catch (err) {
      setError(err instanceof Error ? err.message : "添加批注失败");
    }
  }

  async function toggleResolved(a: PromptAnnotation) {
    const next = !a.resolved;
    setAnnotations((prev) => prev.map((x) => (x.id === a.id ? { ...x, resolved: next ? 1 : 0 } : x)));
    try {
      await setPromptAnnotationResolved(chapter.num, a.id, next);
    } catch {
      setAnnotations((prev) => prev.map((x) => (x.id === a.id ? { ...x, resolved: a.resolved } : x)));
    }
  }

  async function removeAnnotation(a: PromptAnnotation) {
    setAnnotations((prev) => prev.filter((x) => x.id !== a.id));
    try {
      await deletePromptAnnotation(chapter.num, a.id);
    } catch (err) {
      setAnnotations((prev) => [...prev, a].sort((x, y) => x.created_at - y.created_at));
      setError(err instanceof Error ? err.message : "删除批注失败");
    }
  }

  return (
    <>
      <div className="doc-editor-toolbar">
        <div className="doc-editor-modes">
          <button
            className={`btn btn-outlined doc-mode-btn ${mode === "preview" ? "active" : ""}`}
            type="button"
            onClick={() => setMode("preview")}
          >
            预览 / 批注
          </button>
          {isAdmin && (
            <button
              className={`btn btn-outlined doc-mode-btn ${mode === "edit" ? "active" : ""}`}
              type="button"
              onClick={() => setMode("edit")}
            >
              <Pencil size={13} className="icon" />
              <span>编辑</span>
            </button>
          )}
        </div>
        <div className="doc-editor-modes doc-editor-export">
          <button
            className="btn btn-outlined doc-mode-btn"
            type="button"
            title="把本章提示词导出成 Word，可在 Word 里批注、修订"
            onClick={() => downloadPromptsDocx(chapter.num)}
          >
            <Download size={13} className="icon" />
            <span>导出本章 Word</span>
          </button>
          <button
            className="btn btn-outlined doc-mode-btn"
            type="button"
            title="把全部章节的提示词导出成一份 Word"
            onClick={() => downloadPromptsDocx()}
          >
            <Download size={13} className="icon" />
            <span>导出全部</span>
          </button>
        </div>

        {isAdmin ? (
          <button className="btn btn-clay" type="button" disabled={!dirty || saving} onClick={handleSave}>
            <Save size={14} className="icon" />
            <span>{saving ? "保存中…" : dirty ? "保存提示词" : "已保存"}</span>
          </button>
        ) : (
          <span className="doc-editor-readonly-hint">提示词由管理员维护，你可以选中文字添加批注</span>
        )}
      </div>

      {error && <p className="inline-hint doc-editor-error">{error}</p>}

      <div className="doc-editor-body">
        <div className="doc-editor-main">
          {loading ? (
            <p className="admin-empty">加载中…</p>
          ) : !content.trim() && mode === "preview" ? (
            <p className="admin-empty">
              该章节还没有设置提示词{isAdmin ? "，可切换到编辑模式填写" : "，请联系管理员配置"}
            </p>
          ) : mode === "edit" ? (
            <textarea
              className="doc-editor-textarea"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              spellCheck={false}
            />
          ) : (
            <AnnotatedMarkdown
              content={content}
              annotations={annotations}
              onAdd={handleAdd}
              onActivate={setActiveAnnoId}
            />
          )}
        </div>

        <AnnotationSidebar
          title="提示词批注"
          annotations={annotations}
          activeAnnoId={activeAnnoId}
          emptyHint="暂无批注，选中提示词中的文字即可添加"
          meta={(a) => `${a.username} · ${formatTime(a.created_at)}`}
          canDelete={(a) => isAdmin || a.user_id === user.id}
          onToggleResolved={toggleResolved}
          onDelete={removeAnnotation}
        />
      </div>
    </>
  );
}
